import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { 
  Percent, 
  Plus, 
  Edit, 
  Trash2, 
  ArrowLeft, 
  Calendar,
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  Search,
  Filter,
  X
} from 'lucide-react';
import { BASE_URL } from '../config';
import { notify } from '../utils/toast';

export default function Discounts({ onBack }) {
  const [discounts, setDiscounts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    per_page: 10
  });

  // Form state
  const [formData, setFormData] = useState({
    package_id: '',
    discount_percentage: '',
    validity: '',
    status: 'active'
  });

  useEffect(() => {
    fetchDiscounts();
    fetchPackages();
  }, [searchTerm, statusFilter, pagination.current_page]);

  const fetchDiscounts = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        page: pagination.current_page,
        per_page: pagination.per_page,
        search: searchTerm,
        status: statusFilter
      });

      const response = await axios.get(`${BASE_URL}/api/discounts/all/?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDiscounts(response.data.discounts);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      console.error('Error fetching discounts:', err);
      notify.error('Failed to fetch discounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      console.log('🔍 Fetching packages with token:', token ? 'Token exists' : 'No token');
      
      const response = await axios.get(`${BASE_URL}/api/discounts/packages/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('📦 Packages API response:', response.data);
      
      if (response.data.success) {
        setPackages(response.data.packages);
        console.log('✅ Packages loaded:', response.data.packages.length);
      } else {
        console.error('❌ Packages API returned success: false');
        notify.error('Failed to fetch packages');
      }
    } catch (err) {
      console.error('❌ Error fetching packages:', err);
      console.error('❌ Error response:', err.response?.data);
      notify.error('Failed to fetch packages');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('📝 Form submission started');
    console.log('📋 Form data:', formData);
    console.log('✏️ Editing discount:', editingDiscount);
    
    // Validation
    if (!formData.package_id || !formData.discount_percentage || !formData.validity) {
      console.log('❌ Validation failed - missing fields');
      notify.error('Please fill in all required fields');
      return;
    }

    if (formData.discount_percentage < 1 || formData.discount_percentage > 100) {
      console.log('❌ Validation failed - invalid discount percentage');
      notify.error('Discount percentage must be between 1 and 100');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      console.log('🔑 Using token:', token ? 'Token exists' : 'No token');
      
      if (editingDiscount) {
        // Update existing discount
        console.log('🔄 Updating discount ID:', editingDiscount.id);
        const response = await axios.put(
          `${BASE_URL}/api/discounts/update/${editingDiscount.id}/`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log('✅ Update response:', response.data);
        
        if (response.data.success) {
          notify.success('Discount updated successfully');
          fetchDiscounts();
          closeModal();
        }
      } else {
        // Create new discount
        console.log('➕ Creating new discount');
        const response = await axios.post(
          `${BASE_URL}/api/discounts/create/`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log('✅ Create response:', response.data);
        
        if (response.data.success) {
          notify.success('Discount created successfully');
          fetchDiscounts();
          closeModal();
        }
      }
    } catch (err) {
      console.error('❌ Error saving discount:', err);
      console.error('❌ Error response:', err.response?.data);
      const errorMessage = err.response?.data?.error || 'Failed to save discount';
      notify.error(errorMessage);
    }
  };

  const handleDelete = async (discountId) => {
    if (!window.confirm('Are you sure you want to delete this discount?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.delete(
        `${BASE_URL}/api/discounts/delete/${discountId}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        notify.success('Discount deleted successfully');
        fetchDiscounts();
      }
    } catch (err) {
      console.error('Error deleting discount:', err);
      notify.error('Failed to delete discount');
    }
  };

  const openModal = (discount = null) => {
    console.log('🔧 Opening modal with discount:', discount);
    console.log('📦 Available packages:', packages);
    
    if (discount) {
      setEditingDiscount(discount);
      setFormData({
        package_id: discount.package_id,
        discount_percentage: discount.discount_percentage,
        validity: discount.validity ? discount.validity.split('T')[0] : '',
        status: discount.status
      });
      console.log('✏️ Editing mode - form data set:', {
        package_id: discount.package_id,
        discount_percentage: discount.discount_percentage,
        validity: discount.validity ? discount.validity.split('T')[0] : '',
        status: discount.status
      });
    } else {
      setEditingDiscount(null);
      setFormData({
        package_id: '',
        discount_percentage: '',
        validity: '',
        status: 'active'
      });
      console.log('➕ Create mode - form data reset');
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDiscount(null);
    setFormData({
      package_id: '',
      discount_percentage: '',
      validity: '',
      status: 'active'
    });
  };

  const getStatusBadge = (status, isExpired) => {
    if (isExpired) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Clock className="w-3 h-3" />
          Expired
        </span>
      );
    }

    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" />
            Active
          </span>
        );
      case 'inactive':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <AlertCircle className="w-3 h-3" />
            Inactive
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  const columns = [
    {
      name: 'Package',
      selector: row => row.package_name,
      sortable: true,
      cell: row => (
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-blue-600" />
          <div>
            <div className="font-medium text-slate-900">{row.package_name}</div>
            <div className="text-xs text-slate-500">₹{row.package_price}</div>
          </div>
        </div>
      ),
    },
    {
      name: 'Discount',
      selector: row => row.discount_percentage,
      sortable: true,
      cell: row => (
        <div className="flex items-center gap-1">
          <Percent className="w-4 h-4 text-green-600" />
          <span className="font-semibold text-green-600">{row.discount_percentage}%</span>
        </div>
      ),
    },
    {
      name: 'Validity',
      selector: row => row.validity,
      sortable: true,
      cell: row => (
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4 text-slate-600" />
          <span className="text-sm text-slate-700">
            {row.validity ? new Date(row.validity).toLocaleDateString() : 'N/A'}
          </span>
        </div>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      cell: row => getStatusBadge(row.status, row.is_expired),
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => openModal(row)}
            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const customStyles = {
    header: {
      style: {
        minHeight: '56px',
      },
    },
    headRow: {
      style: {
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
        borderTopColor: '#e2e8f0',
        backgroundColor: '#f8fafc',
      },
    },
    headCells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#e2e8f0',
        },
        fontSize: '14px',
        fontWeight: '600',
        color: '#475569',
      },
    },
    cells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#f1f5f9',
        },
        fontSize: '14px',
        color: '#334155',
      },
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary-100 rounded-lg">
                <Percent className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-slate-800">Discount Management</h2>
                <p className="text-sm text-slate-600">Create and manage package discounts</p>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Discount
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search packages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>
          
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none appearance-none bg-white"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <DataTable
            columns={columns}
            data={discounts}
            pagination
            paginationServer
            paginationTotalRows={pagination.total_items}
            paginationPerPage={pagination.per_page}
            paginationRowsPerPageOptions={[10, 25, 50]}
            onChangePage={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
            onChangeRowsPerPage={(perPage) => setPagination(prev => ({ ...prev, per_page: perPage }))}
            customStyles={customStyles}
            highlightOnHover
            pointerOnHover
            responsive
            noDataComponent={
              <div className="flex flex-col items-center justify-center py-12">
                <Percent className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500 text-lg font-medium">No discounts found</p>
                <p className="text-slate-400 text-sm">Create your first discount to get started</p>
              </div>
            }
          />
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            >
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingDiscount ? 'Edit Discount' : 'Create New Discount'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Debug info - remove in production */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="bg-gray-100 p-2 rounded text-xs">
                    <strong>Debug:</strong> Packages loaded: {packages.length}
                    {packages.length > 0 && (
                      <div>First package: {packages[0].package_name}</div>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Package *
                  </label>
                  <select
                    value={formData.package_id}
                    onChange={(e) => {
                      console.log('📦 Package selected:', e.target.value);
                      setFormData({ ...formData, package_id: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    required
                    disabled={editingDiscount} // Don't allow changing package when editing
                  >
                    <option value="">Select a package</option>
                    {packages.length === 0 ? (
                      <option value="" disabled>Loading packages...</option>
                    ) : (
                      packages.map((pkg) => (
                        <option key={pkg.package_id} value={pkg.package_id}>
                          {pkg.package_name} (₹{pkg.price})
                        </option>
                      ))
                    )}
                  </select>
                  {packages.length === 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      No packages available. Please check your connection.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Discount Percentage *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    placeholder="Enter discount percentage (1-100)"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Valid Until *
                  </label>
                  <input
                    type="date"
                    value={formData.validity}
                    onChange={(e) => setFormData({ ...formData, validity: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="flex items-center gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors font-medium"
                  >
                    {editingDiscount ? 'Update Discount' : 'Create Discount'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
}